/**
 * Custom emoji.
 *
 * Two steps on the server - upload the image, then claim a name for it - so the
 * form does both from one button and only shows an error once, whichever half
 * failed.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fileUrl, serverUrl, socket } from '@/lib/api';
import { setCustomEmoji, type CustomEmoji } from '@/lib/emoji';
import { Button, Card, CardHead, Field, Input, useConfirm, useToast } from '@/components/ui';
import { Select } from '@/components/Select';
import * as Icons from '@/icons';
import { useEffect, useRef } from 'react';

export function EmojiAdmin() {
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  /* Who gets it. Set here so a reserved emoji is never briefly open to
     everybody between being uploaded and being restricted. */
  const [access, setAccess] = useState('everyone');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const { data } = useQuery({
    queryKey: ['emoji'],
    queryFn: () => api.get<{ emoji: CustomEmoji[] }>('/api/emoji')
  });

  // The picker reads from a module-level cache, so it has to be told when the
  // set changes - including when another moderator changes it.
  useEffect(() => {
    if (data?.emoji) setCustomEmoji(data.emoji);
  }, [data]);

  useEffect(() => socket.on('emoji:changed', (d: { emoji: CustomEmoji[] }) => {
    setCustomEmoji(d.emoji);
    void queryClient.invalidateQueries({ queryKey: ['emoji'] });
  }), [queryClient]);

  async function add() {
    if (!file) { toast('Pick an image first', 'warn'); return; }
    setBusy(true);
    try {
      const res = await fetch(serverUrl() + '/api/files', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''),
          'Content-Type': file.type,
          'X-File-Type': file.type,
          'X-File-Name': encodeURIComponent(file.name),
          'X-File-Kind': 'emoji'
        },
        body: file
      });
      const up = await res.json() as { file?: { id: string }; error?: string };
      if (!up.file) throw new Error(up.error ?? 'Upload failed');

      await api.post('/api/admin/emoji', { name, fileId: up.file.id, access });
      toast(':' + name + ': added', 'good');
      setName('');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      await queryClient.invalidateQueries({ queryKey: ['emoji'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function remove(emoji: CustomEmoji) {
    const ok = await confirm('Delete :' + emoji.name + ':?', 'Messages already using it fall back to the plain text.', {
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await api.del('/api/admin/emoji/' + emoji.id);
      await queryClient.invalidateQueries({ queryKey: ['emoji'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const list = data?.emoji ?? [];
  const clean = name.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 24);

  return (
    <>
      <Card>
        <CardHead title="Add a custom emoji" note="Square images work best. 200 maximum." />
        <div className="grid items-end gap-[var(--gap)] sm:grid-cols-[1fr_1fr_1fr_auto]">
          <Field label="Name" hint={clean ? 'Typed as :' + clean + ':' : 'Letters, numbers and _'}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={24}
              placeholder="poggers"
              spellCheck={false}
            />
          </Field>
          <Field label="Image">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="h-[var(--h-md)] w-full rounded-[var(--radius-s)] border border-line bg-surface-1 px-3 py-1.5 text-[12.5px] file:mr-3 file:rounded file:border-0 file:bg-surface-3 file:px-2.5 file:py-1 file:text-[12px] file:font-semibold file:text-ink"
            />
          </Field>
          <Field label="Who can use it">
            <Select
              search={false}
              value={access}
              onChange={setAccess}
              options={[
                { value: 'everyone', label: 'Everyone', icon: 'globe' },
                { value: 'premium', label: 'Premium and above', icon: 'badgePremium', color: 'var(--tier-epic)' },
                { value: 'vip', label: 'VIP only', icon: 'badgeVip', color: 'var(--tier-mythic)' },
                { value: 'staff', label: 'Staff only', icon: 'shieldNav' }
              ]}
            />
          </Field>
          <Button kind="primary" disabled={busy || clean.length < 2 || !file} onClick={() => void add()}>
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </Card>

      <Card>
        <CardHead title="Custom emoji" note={list.length + ' of 200'} />
        {list.length ? (
          <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
            {list.map((e) => (
              <div
                key={e.id}
                className="flex items-center gap-2.5 rounded-[var(--radius-s)] border border-line-soft bg-surface-1 p-2.5"
              >
                <img src={fileUrl(e.url)} alt={':' + e.name + ':'} className="h-8 w-8 flex-none object-contain" />
                <span className="min-w-0 flex-1 truncate font-mono text-[12px]">:{e.name}:</span>
                <button
                  type="button"
                  title="Delete"
                  onClick={() => void remove(e)}
                  className="grid h-7 w-7 flex-none place-items-center rounded-md text-mute transition-colors hover:bg-[color-mix(in_srgb,var(--bad)_18%,transparent)] hover:text-bad"
                >
                  <Icons.Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-[13px] text-mute">No custom emoji yet.</p>
        )}
      </Card>
      {dialog}
    </>
  );
}
